import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import ProfawzLogo from "../components/ProfawzLogo";
import { Calendar, Clock, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function PublicAgenda() {
    const { inviteCode } = useParams();
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [done, setDone] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/public/agenda/${inviteCode}`);
                setInfo(data);
            } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Lien invalide");
            } finally { setLoading(false); }
        })();
    }, [inviteCode]);

    if (loading) return <Center>Chargement…</Center>;
    if (!info) return <Center><h1 className="h-display text-2xl">Lien invalide</h1></Center>;

    return (
        <div className="min-h-screen bg-paper">
            <header className="border-b border-rule bg-white">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
                    <ProfawzLogo size={28} />
                    <div>
                        <div className="h-display text-base font-bold">Profawz</div>
                        <div className="text-[10px] font-mono text-muted2 uppercase tracking-wider">Réserver un rendez-vous</div>
                    </div>
                </div>
            </header>
            <main className="max-w-3xl mx-auto px-6 py-10" data-testid="public-agenda-page">
                {done ? (
                    <div className="panel p-10 text-center" data-testid="public-agenda-done">
                        <CheckCircle2 size={42} className="text-emerald-500 mx-auto mb-3" />
                        <h2 className="h-display text-2xl font-semibold">Rendez-vous confirmé</h2>
                        <p className="text-sm text-muted2 mt-2 max-w-md mx-auto">
                            Un e-mail de confirmation peut être envoyé. Le responsable de formation a bien reçu votre demande.
                        </p>
                    </div>
                ) : selected ? (
                    <BookingForm
                        slot={selected}
                        onCancel={() => setSelected(null)}
                        onDone={() => setDone(true)}
                    />
                ) : (
                    <>
                        <div className="label-eyebrow mb-2">{info.establishment}</div>
                        <h1 className="h-display text-3xl sm:text-4xl font-semibold leading-tight mb-2">
                            Réservez un créneau avec<br /><span className="text-orange">{info.responsable_name}</span>
                        </h1>
                        <p className="text-sm text-muted2 mb-6">Sélectionnez un créneau ci-dessous puis remplissez vos coordonnées.</p>

                        {info.slots.length === 0 ? (
                            <div className="panel p-10 text-center text-sm text-muted2" data-testid="public-agenda-no-slots">
                                Aucun créneau disponible pour le moment. Contactez le responsable de formation par e-mail.
                            </div>
                        ) : (
                            <div className="grid sm:grid-cols-2 gap-3">
                                {info.slots.map((s) => (
                                    <button
                                        key={s.id}
                                        onClick={() => setSelected(s)}
                                        className="panel p-4 text-left hover:border-orange transition-colors"
                                        data-testid={`public-slot-${s.id}`}
                                    >
                                        <div className="text-xs font-mono text-muted2 flex items-center gap-1.5"><Calendar size={12} /> {s.date}</div>
                                        <div className="h-display text-lg font-semibold mt-1 flex items-center gap-1.5"><Clock size={14} /> {s.start_time} → {s.end_time}</div>
                                        <div className="text-xs text-muted2 mt-1">{s.title}</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </main>
        </div>
    );
}

function BookingForm({ slot, onCancel, onDone }) {
    const [tutor_prenom, setPrenom] = useState("");
    const [tutor_nom, setNom] = useState("");
    const [tutor_email, setEmail] = useState("");
    const [tutor_phone, setPhone] = useState("");
    const [company, setCompany] = useState("");
    const [alternant_name, setAlternant] = useState("");
    const [visio_link, setVisio] = useState("");
    const [message, setMessage] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!tutor_prenom.trim() || !tutor_nom.trim() || !tutor_email.trim()) {
            toast.error("Prénom, nom et e-mail requis");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/public/agenda/book/${slot.id}/${slot.public_token}`, {
                tutor_prenom, tutor_nom, tutor_email, tutor_phone,
                company, alternant_name, visio_link, message,
            });
            toast.success("Réservation confirmée !");
            onDone();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        } finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} className="panel p-6" data-testid="public-booking-form">
            <button type="button" onClick={onCancel} className="text-xs font-mono text-muted2 inline-flex items-center gap-1 hover:text-ink mb-4">
                <ArrowLeft size={12} /> Choisir un autre créneau
            </button>
            <div className="label-eyebrow mb-1">Créneau choisi</div>
            <h2 className="h-display text-xl font-semibold mb-5">{slot.date} · {slot.start_time} → {slot.end_time}</h2>

            <div className="grid sm:grid-cols-2 gap-3">
                <div>
                    <label className="label-eyebrow mb-2 block">Prénom *</label>
                    <input className="field-input" value={tutor_prenom} onChange={(e) => setPrenom(e.target.value)} required data-testid="booking-prenom-input" />
                </div>
                <div>
                    <label className="label-eyebrow mb-2 block">Nom *</label>
                    <input className="field-input" value={tutor_nom} onChange={(e) => setNom(e.target.value)} required data-testid="booking-nom-input" />
                </div>
                <div>
                    <label className="label-eyebrow mb-2 block">E-mail *</label>
                    <input type="email" className="field-input" value={tutor_email} onChange={(e) => setEmail(e.target.value)} required data-testid="booking-email-input" />
                </div>
                <div>
                    <label className="label-eyebrow mb-2 block">Téléphone</label>
                    <input type="tel" className="field-input" value={tutor_phone} onChange={(e) => setPhone(e.target.value)} placeholder="06 12 34 56 78" data-testid="booking-phone-input" />
                </div>
                <div>
                    <label className="label-eyebrow mb-2 block">Entreprise</label>
                    <input className="field-input" value={company} onChange={(e) => setCompany(e.target.value)} data-testid="booking-company-input" />
                </div>
                <div>
                    <label className="label-eyebrow mb-2 block">Alternant concerné</label>
                    <input className="field-input" value={alternant_name} onChange={(e) => setAlternant(e.target.value)} placeholder="Prénom Nom" data-testid="booking-alternant-input" />
                </div>
                <div className="sm:col-span-2">
                    <label className="label-eyebrow mb-2 block">Lien visio (Meet, Teams, Zoom…)</label>
                    <input type="url" className="field-input" value={visio_link} onChange={(e) => setVisio(e.target.value)} placeholder="https://meet.google.com/…" data-testid="booking-visio-input" />
                </div>
                <div className="sm:col-span-2">
                    <label className="label-eyebrow mb-2 block">Message (optionnel)</label>
                    <textarea rows={3} className="field-input resize-none" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="booking-message-input" />
                </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
                <button type="button" onClick={onCancel} className="btn-outline">Annuler</button>
                <button type="submit" disabled={saving} className="btn-primary" data-testid="booking-submit-button">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    {saving ? "Réservation…" : "Confirmer le rendez-vous"}
                </button>
            </div>
        </form>
    );
}

const Center = ({ children }) => (
    <div className="min-h-screen bg-paper flex items-center justify-center px-6 text-center text-muted2">
        {children}
    </div>
);
